import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Globe, BarChart3, Gauge, Loader, CircleCheck, TriangleAlert, KeyRound, Copy, BookOpen } from 'lucide-react';
import { Card, CardBody, IconButton, LinkCard } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { toneColor } from './shared';

export interface DomainCounts {
  total: number;
  active: number;
  deploying: number;
  pending: number;
}

export interface ReachSummary {
  /** Distinct countries with edge traffic in the period. */
  countries: number;
  /** Distinct CDN providers serving traffic. */
  providers: number;
  /** Busiest country by requests. */
  topRegion?: string;
  /** Top country's share of total requests (%). */
  topRegionShare?: number;
}

export interface ApiInfo {
  /** CDN analytics API base URL. */
  endpoint?: string;
  /** Signed-in account id. */
  accountId?: string;
}

/** 5xx share above this reads as "needs attention". */
const ERROR_RATE_THRESHOLD = 0.01;
const fmtRate = (r: number) => `${(r * 100).toFixed(r >= 0.1 ? 1 : 2)}%`;

/** A titled rail group separated by a hairline rule (recipe rail idiom). */
function RailSection({ title, children, divider = true }: { title: string; children: ReactNode; divider?: boolean }) {
  return (
    <section
      style={{
        paddingBottom: divider ? 'var(--vn-space-4)' : 0,
        borderBottom: divider ? '1px solid var(--vn-border-subtle, var(--vn-border-default))' : undefined,
        display: 'grid',
        gap: 'var(--vn-space-2)',
      }}
    >
      <h3 style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-default)', margin: 0 }}>{title}</h3>
      {children}
    </section>
  );
}

/** Label + value, with an optional status dot. */
function InfoRow({ label, value, dot }: { label: string; value: ReactNode; dot?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {dot ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} /> : null}
        {label}
      </span>
      <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-semibold)', textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

/** Tidy left-aligned nav row — icon + label, no stretch. An `href` makes it an
 *  external link (new tab); otherwise it's a button driving in-app navigation. */
function RailLink({ icon, label, onClick, href }: { icon: ReactNode; label: string; onClick?: () => void; href?: string }) {
  const inner = (
    <>
      <span style={{ color: 'var(--vn-fg-muted)', display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
    </>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" style={railLink}>{inner}</a>
  ) : (
    <button type="button" onClick={onClick} style={railLink}>{inner}</button>
  );
}

/** Public API reference (Postman). */
const POSTMAN_DOCS_URL = 'https://postman.vnetwork.dev/view/4429080/2sBY4Qqyts';

const railLink: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  padding: 'var(--vn-space-0) 0',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  textAlign: 'left',
  width: 'fit-content',
};

/** Read-only ID/endpoint with a copy-to-clipboard button. */
function CopyField({ label, value }: { label: string; value: string }) {
  const { show } = useToast();
  const copy = () => {
    navigator.clipboard?.writeText(value).then(
      () => show({ tone: 'success', title: 'Copied' }),
      () => show({ tone: 'danger', title: 'Copy failed' }),
    );
  };
  return (
    <div>
      <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-0)' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <code style={{ fontSize: 'var(--vn-font-size-xs)', fontFamily: 'var(--vn-font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value}
        </code>
        <IconButton aria-label={`Copy ${label}`} variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} />
        </IconButton>
      </div>
    </div>
  );
}

/**
 * Contextual rail for the Manticore overview: an always-visible domain-health
 * breakdown, an actionable "needs attention" surface (domains still rolling
 * out) and quick jump-offs to the deeper edge surfaces. Light hairline-ruled
 * sections rather than nested cards, to read as secondary to the main column.
 */
export function ManticoreOverviewRail({
  counts,
  reach,
  reachLoading,
  errorRate = 0,
  errorRateLoading,
  api,
  base,
}: {
  counts: DomainCounts;
  reach?: ReachSummary;
  reachLoading?: boolean;
  errorRate?: number;
  errorRateLoading?: boolean;
  api?: ApiInfo;
  /** Scoped CDN console base path, e.g. `/services/cdn/:id`. Drives quick links. */
  base: string;
}) {
  const navigate = useNavigate();
  const rollout = counts.deploying + counts.pending;
  const errorsHigh = errorRate >= ERROR_RATE_THRESHOLD;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <RailSection title="Domain health">
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <InfoRow label="Total" value={counts.total} />
          <InfoRow label="Active" value={counts.active} dot={toneColor('success')} />
          <InfoRow label="Deploying" value={counts.deploying} dot={toneColor('info')} />
          <InfoRow label="Pending" value={counts.pending} dot={toneColor('warning')} />
        </div>
      </RailSection>

      <RailSection title="Reach">
        {reachLoading ? (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Loading…</span>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <InfoRow label="Countries served" value={reach?.countries ?? 0} />
            <InfoRow label="CDN providers" value={reach?.providers ?? 0} />
            <InfoRow
              label="Top region"
              value={reach?.topRegion ? `${reach.topRegion}${reach.topRegionShare != null ? ` · ${reach.topRegionShare}%` : ''}` : '—'}
            />
          </div>
        )}
      </RailSection>

      <RailSection title="Needs attention">
        {errorRateLoading ? (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Loading…</span>
        ) : errorsHigh ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
              <TriangleAlert size={15} color={toneColor('warning')} style={{ flexShrink: 0 }} />
              {fmtRate(errorRate)} of requests returned 5xx.
            </span>
            <RailLink icon={<ArrowRight size={15} />} label="Investigate in analytics" onClick={() => navigate(`${base}/analytics`)} />
          </div>
        ) : rollout > 0 ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
              <Loader size={15} color={toneColor('info')} style={{ flexShrink: 0 }} />
              {rollout} domain{rollout === 1 ? '' : 's'} still rolling out.
            </span>
            <RailLink icon={<ArrowRight size={15} />} label="Review domains" onClick={() => navigate(`${base}/domains`)} />
          </div>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            <CircleCheck size={15} color={toneColor('success')} style={{ flexShrink: 0 }} />
            Healthy — {fmtRate(errorRate)} 5xx errors.
          </span>
        )}
      </RailSection>

      <RailSection title="Quick links" divider={!!api?.endpoint || !!api?.accountId}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <LinkCard
            icon={<Globe size={16} />}
            title="Domains"
            description="Caching, origins, rules & TLS"
            href={`${base}/domains`}
            onClick={(e) => { e.preventDefault(); navigate(`${base}/domains`); }}
          />
          <LinkCard
            icon={<BarChart3 size={16} />}
            title="Analytics"
            description="Traffic, cache & security insights"
            href={`${base}/analytics`}
            onClick={(e) => { e.preventDefault(); navigate(`${base}/analytics`); }}
          />
          <LinkCard
            icon={<Gauge size={16} />}
            title="Business usage"
            description="Per-domain egress, billed monthly"
            href={`${base}/usage`}
            onClick={(e) => { e.preventDefault(); navigate(`${base}/usage`); }}
          />
        </div>
      </RailSection>

      {(api?.endpoint || api?.accountId) && (
        <Card flat>
          <CardBody>
            <h3 style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-default)', margin: '0 0 var(--vn-space-2)' }}>API</h3>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {api.endpoint ? <CopyField label="API endpoint" value={api.endpoint} /> : null}
              {api.accountId ? <CopyField label="Account ID" value={api.accountId} /> : null}
            </div>
            <div style={{ marginTop: 'var(--vn-space-3)', display: 'grid', gap: 'var(--vn-space-1)' }}>
              <RailLink icon={<KeyRound size={15} />} label="Manage API keys" onClick={() => navigate('/account/api-keys')} />
              <RailLink icon={<BookOpen size={15} />} label="API documentation" href={POSTMAN_DOCS_URL} />
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
